/* eslint eslint-comments/no-use: off */

import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {useDebounce} from '@github-ui/use-debounce'
import {GitBranchIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback, useMemo, useRef, useState} from 'react'

import styles from './BranchPicker.module.css'

export type BranchPickerBranchFuture = {
  id: string
  name: string
}

type BranchPickerProps<TBranchType extends BranchPickerBranchFuture> = {
  items: TBranchType[]
  initialSelectedItem?: TBranchType | null
  defaultBranchId: string | undefined
  onSelectionChange: (selected?: TBranchType) => void
  loading?: boolean
  title?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  fetchSearchData: (nextValue: string) => void
}

export function BranchPicker<TBranchType extends BranchPickerBranchFuture>({
  fetchSearchData,
  ...rest
}: BranchPickerProps<TBranchType>) {
  const [filter, setFilter] = useState('')

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) return

      debounceFetchSearchData(trimmedFilter)
      setFilter(value)
    },
    [debounceFetchSearchData, filter],
  )
  return <BranchPickerBase {...rest} onFilter={filterItems} />
}

type BranchPickerBaseProps<TBranchType extends BranchPickerBranchFuture> = Omit<
  BranchPickerProps<TBranchType>,
  'fetchSearchData'
> & {
  onFilter: (value: string) => void
}

export function BranchPickerBase<TBranchType extends BranchPickerBranchFuture>({
  items,
  initialSelectedItem,
  onFilter,
  defaultBranchId,
  onSelectionChange,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  title,
  loading = false,
}: BranchPickerBaseProps<TBranchType>) {
  const filterItems = useCallback(
    (value: string) => {
      onFilter(value)
    },
    [onFilter],
  )

  const getItemKey = useCallback((branch: TBranchType) => branch.id, [])

  const convertToItemProps = useCallback(
    (branch: TBranchType): ExtendedItemProps<TBranchType> => ({
      // this is a hack to make sure that we are using the prop
      id: `${branch.id}`,
      children: <span>{branch.name}</span>,
      source: branch,
      leadingVisual: () => <Octicon icon={GitBranchIcon} size={16} />,
      trailingVisual: () => (branch.id === defaultBranchId ? <span>Default</span> : null),
    }),
    [defaultBranchId],
  )

  const branchPickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => (
      <Button
        leadingVisual={GitBranchIcon}
        trailingVisual={TriangleDownIcon}
        aria-label={!ariaLabelledBy ? LABELS.selectBranch : undefined}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        {...anchorProps}
        ref={branchPickerRef}
      >
        {initialSelectedItem ? <span>{initialSelectedItem.name}</span> : LABELS.selectBranch}
      </Button>
    ),
    [ariaDescribedBy, ariaLabelledBy, initialSelectedItem],
  )

  const showNoMatchMessage = useMemo(() => items.length === 0, [items])

  return (
    <div className={styles.branchPickerContainer}>
      <ItemPicker
        items={items}
        initialSelectedItems={initialSelectedItem ? [initialSelectedItem] : []}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.selectBranch}
        selectionVariant="single"
        onSelectionChange={([branch]) => onSelectionChange(branch)}
        loading={loading}
        renderAnchor={renderAnchor}
        selectPanelRef={branchPickerRef}
        enforceAtleastOneSelected
        resultListAriaLabel={'Branch results'}
        title={title ?? 'Select a branch'}
        width={'large'}
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No branches were found',
                variant: 'empty',
                body: 'Try searching with a different query for results.',
              }
            : undefined
        }
      />
    </div>
  )
}

export const BranchPickerPlaceholder = ({currentBranch}: {currentBranch?: string | null}) => (
  <Button leadingVisual={GitBranchIcon} trailingVisual={TriangleDownIcon} disabled>
    {currentBranch ?? LABELS.selectBranch}
  </Button>
)

try{ BranchPicker.displayName ||= 'BranchPicker' } catch {}
try{ BranchPickerBase.displayName ||= 'BranchPickerBase' } catch {}
try{ BranchPickerPlaceholder.displayName ||= 'BranchPickerPlaceholder' } catch {}